// TUDO O QUE UMA CRIANÇA ESCREVEU DENTRO DO QUADRINHO, COM O LUGAR DE CADA PEDAÇO.
//
// ── POR QUE ESTA PEÇA EXISTE ────────────────────────────────────────────────────────────────
//
// A barreira do texto da história está construída e testada no backend desde 09/09
// (`moderation/texto-de-historia.ts`, `POST /moderar-texto`), e até hoje NADA a chamava. O nó
// `BL-2.5` registrava o porquê, e o porquê era honesto: *o ato de publicar não existia*. Ligar
// uma barreira num fluxo que não existe é trabalho sem alvo.
//
// O fluxo nasceu em 12/09 (`PL-T2.7`, esta mesma pasta). Então a barreira se liga agora, e é
// aqui — no caminho de publicar — porque é exatamente aí que o texto da criança deixa de ser
// dela e vira conteúdo lido por outras turmas da escola. Essa é a razão de a barreira existir, e
// está escrita no comentário da rota do backend.
//
// ── POR QUE CONFERIR NO PUBLICAR E NÃO NO EDITOR ────────────────────────────────────────────
//
// No editor, a criança está PENSANDO. Uma barreira que interrompe a cada letra digitada ensina a
// ela que a plataforma está vigiando o rascunho dela, e o efeito prático é ela parar de escrever.
// No publicar, a pergunta muda de "o que você está escrevendo?" para "isto pode ser lido pela
// escola inteira?" — e essa pergunta é legítima, porque a resposta é sim ou não para todo mundo.
//
// ── O LUGAR, E NÃO SÓ O VEREDICTO ───────────────────────────────────────────────────────────
//
// Uma recusa que diz "tem palavra proibida no seu quadrinho" e não diz ONDE obriga a criança a
// caçar num quadrinho de dez páginas. Por isso cada texto vai com o endereço dele em português
// — "página 3, balão 2" —, e a tela devolve esse endereço junto com a recusa.

import { DocumentoHQ, Elemento, Pagina } from "../api/documento.types";

export interface TextoLocalizado {
  /** O que a criança escreveu. É o que vai para a barreira. */
  texto: string;
  /** Onde ele está, em português, para a recusa poder apontar. */
  onde: string;
}

/** Rótulo por tipo de elemento — o que a criança chama a coisa, não o que o código chama. */
const NOME_DO_TIPO: Record<string, string> = {
  balao: "balão",
  texto: "texto",
};

/**
 * Os elementos que CARREGAM texto escrito por pessoa.
 *
 * Só `balao` e `texto`. `imagem`, `forma`, `quadro` e `personagem` têm rótulos e identificadores,
 * e nenhum deles foi digitado por alguém — mandá-los para a barreira gastaria a chamada e
 * produziria recusa por nome de arquivo, que é o tipo de falso positivo que faz uma escola
 * desligar a moderação inteira.
 */
function ehElementoComTexto(e: Elemento): e is Extract<Elemento, { texto: string }> {
  return (e.tipo === "balao" || e.tipo === "texto") && typeof e.texto === "string";
}

function textosDaPagina(pagina: Pagina, numero: number): TextoLocalizado[] {
  const contador: Record<string, number> = {};
  const achados: TextoLocalizado[] = [];

  // A LEGENDA DA PÁGINA TAMBÉM É TEXTO DE PESSOA, e é fácil de esquecer porque não está dentro de
  // camada nenhuma — ela é um campo da própria página.
  const legenda = (pagina.legenda || "").trim();
  if (legenda) achados.push({ texto: legenda, onde: `página ${numero}, legenda` });

  // Os elementos moram dentro das CAMADAS, e não soltos na página. A contagem atravessa as
  // camadas de propósito: a criança vê "o segundo balão desta página", e não sabe — nem precisa
  // saber — em que camada ele foi desenhado.
  for (const camada of pagina.camadas || []) {
    for (const elemento of camada.elementos || []) {
      if (!ehElementoComTexto(elemento)) continue;
      const conteudo = elemento.texto.trim();
      if (!conteudo) continue;

      // A numeração é POR TIPO e na ordem em que os elementos estão no documento, que é a ordem
      // em que o editor os desenha. "balão 2" quer dizer o segundo balão daquela página — e não
      // o segundo elemento, que incluiria imagens e quadros que a criança não conta.
      const tipo = NOME_DO_TIPO[elemento.tipo] || elemento.tipo;
      contador[tipo] = (contador[tipo] || 0) + 1;
      achados.push({ texto: conteudo, onde: `página ${numero}, ${tipo} ${contador[tipo]}` });
    }
  }

  return achados;
}

/**
 * Todo texto escrito por pessoa no quadrinho, do título ao último balão.
 *
 * O TÍTULO VAI JUNTO, e é o pedaço mais exposto de todos: é ele que aparece na listagem da
 * biblioteca para quem nunca abriu o quadrinho. Um título ofensivo é lido por mais gente do que
 * qualquer balão lá dentro.
 */
export function textosDoQuadrinho(titulo: string, documento: DocumentoHQ): TextoLocalizado[] {
  const tudo: TextoLocalizado[] = [];

  const tituloLimpo = (titulo || "").trim();
  if (tituloLimpo) tudo.push({ texto: tituloLimpo, onde: "o título" });

  (documento.paginas || []).forEach((pagina, i) => {
    tudo.push(...textosDaPagina(pagina, i + 1));
  });

  return tudo;
}

/**
 * O mesmo, para uma lista solta de páginas.
 *
 * A tela de publicar trabalha com `Pagina[]` já convertida, e não com o `DocumentoHQ` inteiro.
 * Duas portas para o mesmo cálculo, em vez de a tela montar um documento falso só para chamar a
 * função de cima.
 */
export function textosDasPaginas(titulo: string, paginas: Pagina[]): TextoLocalizado[] {
  return textosDoQuadrinho(titulo, { paginas } as DocumentoHQ);
}
